/**
 * @typedef {{ size: number; targets: WeakMap<HTMLElement, (intersectionEntry: IntersectionObserverEntry) => boolean>; observer: IntersectionObserver; }} ObserverCacheEntry
 * @typedef {{ unobserve: () => void; }} ObserveIntersectionController
 */

import $ from 'jquery';
import { getXPathForElement } from '../dom.js';
import { nextTick } from '../event-loop.js';
import IntersectionObserverMultiton from './IntersectionObserverMultiton.js';
import { getIntersectionObserverInitDefaults } from './intersection-observer-defaults.js';

const observerOptionNames = new Set(['root', 'rootMargin', 'threshold']);
const serializeObserverOptions = (options) => {
	// Order the options first
	const orderedOptions = {};
	const orderedKeys = Object.keys(options).filter(opt => observerOptionNames.has(opt)).sort();
	for (const key of orderedKeys) {
		if (key === 'root' && options[key]) {
			orderedOptions[key] = getXPathForElement(options[key]);
			continue;
		}
		orderedOptions[key] = options[key];
	}
	return JSON.stringify(orderedOptions);
};

/**
 * Checks wether an element is in viewport
 *
 * @link https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 * @link https://pawelgrzybek.com/the-intersection-observer-api-explained/
 *
 * @param {HTMLElement} element
 * @param {boolean} isFullyInView
 * @param {IntersectionObserverInit} observerInit
 * @param {(intersectionEntry: IntersectionObserverEntry) => boolean} onChange
 * @param {((arg0: Error) => void)=} onError
 *
 * @returns {ObserveIntersectionController}
 */
export const observeIntersections = (element, isFullyInView, observerInit, onChange, onError) => {

	const options = {
		...getIntersectionObserverInitDefaults(element, isFullyInView),
		...observerInit,
	};
	const serializedOptions = serializeObserverOptions(options);
	const observer = IntersectionObserverMultiton.getOrCreateObserver(options, serializedOptions);

	// Call the observer with a small delay to make sure the element is drawn
	nextTick(() => {
		try {
			observer.observe(element, onChange);
		}
		catch ( /** @type {any} */error) {
			// Element is not in the DOM
			console.warn(error);

			// Cast as error
			if (!(error instanceof Error)) {
				// @ts-ignore
				// eslint-disable-next-line no-ex-assign
				error = new Error(error);
			}
			onError && onError(error);
		}
	});

	return {
		unobserve: () => {
			nextTick(() => {
				observer.unobserve(element);
			});
		},
	};
};

/**
 * Checks wether an element is in viewport
 *
 * @link https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 * @link https://pawelgrzybek.com/the-intersection-observer-api-explained/
 *
 * @param {HTMLElement} element
 * @param {boolean} isFullyInView
 * @param {IntersectionObserverInit} observerInit
 * @param {(intersectionEntry: IntersectionObserverEntry) => boolean} onChange
 * @param {((arg0: Error) => void)=} onError
 */
export const observeElementIntersection = (element, isFullyInView, observerInit, onChange, onError) => {

	let isObserving = false;

	const observer = new IntersectionObserver((/** @type {IntersectionObserverEntry[]} */[entry]) => {

		// Some older browser didn't implement isIntersecting
		if (typeof entry.isIntersecting === 'undefined') {
			// @ts-ignore
			entry.isIntersecting = entry.intersectionRatio > 0;
		}

		// Some useful properties
		// const ratio = entry.intersectionRatio;
		// if (ratio === 0) {
		// 	entry.postion = 'out';
		// }
		// else if (ratio < 1) {
		// 	entry.postion = (entry.boundingClientRect.top < entry.intersectionRect.top) ? 'top' : 'bottom';
		// }
		// else {
		// 	entry.postion = 'in';
		// }

		// Call the onChange callback and if it returns true, disconnect the observer
		if (onChange(entry)) {
			observer.disconnect();
			isObserving = false;
		}
	}, {
		...getIntersectionObserverInitDefaults(element, isFullyInView),
		...observerInit,
	});

	// Call the observer with a small delay to make sure the element is drawn
	nextTick(() => {
		try {
			isObserving = true;
			observer.observe(element);
			$(element).one('destroy', () => {
				if (isObserving === true) {
					observer.disconnect();
					isObserving = false;
					console.warn('Observed element has been destroyed (element: %o)', element);
					// onError && onError(new Error('Observed element has been destroyed'));
				}
			});
		}
		catch ( /** @type {any} */error) {
			// Element is not in the DOM
			console.warn(error);

			// Cast as error
			if (!(error instanceof Error)) {
				// @ts-ignore
				// eslint-disable-next-line no-ex-assign
				error = new Error(error);
			}
			onError && onError(error);
		}
	});

	return observer;
};

/**
 * Check wether an element is in viewport, async (no reflow done)
 *
 * @param {HTMLElement} element
 * @param {IntersectionObserverInit} observerInit
 */
export const isElementInViewAsync = (element, observerInit = {}, isFullyInView = false) => {
	return new Promise((resolve, reject) => {
		observeElementIntersection(element, isFullyInView, observerInit, (entry) => {
			// console.log('isIntersecting: %o, ratio: %o', entry.isIntersecting, entry.intersectionRatio);
			resolve(entry.isIntersecting && entry.intersectionRatio > (isFullyInView ? 0.9999 : 0));
			return true;
		}, reject);
	});
};

/**
 * Execute when an element is in viewport
 *
 * @param {HTMLElement} element
 * @param {IntersectionObserverInit} observerInit
 */
export const whenElementInView = (element, observerInit = {}, isFullyInView = false) => {
	return new Promise((resolve, reject) => {
		observeElementIntersection(element, isFullyInView, observerInit, (entry) => {
			if (entry.isIntersecting && entry.intersectionRatio > (isFullyInView ? 0.9999 : 0)) {
				resolve(element);
				return true;
			}
			return false;
		}, reject);
	});
};

/**
 * Execute when an element is not in viewport
 *
 * @link https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 * @link https://pawelgrzybek.com/the-intersection-observer-api-explained/
 *
 * @param {HTMLElement} element
 * @param {IntersectionObserverInit} options
 */
export const whenElementNotInView = (element, options = {}, isFullyInView = false) => {
	return new Promise((resolve, reject) => {
		observeElementIntersection(element, isFullyInView, options, (entry) => {
			if ((entry.isIntersecting === false) && entry.intersectionRatio <= (isFullyInView ? 1 : 0)) {
				resolve(element);
				return true;
			}
			return false;
		}, reject);
	});
};

/**
 * Alternate function to Element.getBoundingClientRect() that runs in another thread and does not do a reflow
 *
 * @param {HTMLElement} element
 */
export const getElementRectNoReflow = (element) => {
	return new Promise((resolve, reject) => {
		observeElementIntersection(element, false, undefined, (entry) => {
			resolve(entry.boundingClientRect);
			return true;
		}, reject);
	});
};
